import {
  Children,
  cloneElement,
  isValidElement,
  useState,
  useEffect,
  useRef,
  createRef,
  forwardRef,
  RefObject,
  ReactElement,
} from 'react'
import styles from '@/styles/components/carousel/SimpleCarousel.module.scss'
import cn from 'classnames'

interface Props {
  loop?: boolean,
  animationSpeed?: number,
  width?: number | 'auto',
  autoplay?: boolean,
  gap?: number,
  startIndex?: number,
  doubleGap?: number,
  endLess?: boolean,
  bullets?: boolean,
  bulletsClass?: string,
  overflow?: 'visible' | 'hidden',
  justifyBetween?: boolean
  containerClass?: string
  minSwipeDistance?: number,
  disableControls?: boolean,
  controlsVisible?: boolean,
  nextHtml?: JSX.Element | undefined,
  prevHtml?: JSX.Element | undefined,
  nextClass?: string,
  prevClass?: string,
  onSlideChange?(index: number): void
}

const noop = () => {
  return
}

export const SimpleCarousel: React.FC<Props> = ({
  children,
  loop = false,
  animationSpeed = 600,
  width = 'auto',
  autoplay = false,
  gap = 0,
  startIndex = 0,
  doubleGap = 0,
  endLess = false,
  justifyBetween = false,
  containerClass = '',
  minSwipeDistance = 10,
  disableControls = false,
  bullets = false,
  bulletsClass = '',
  overflow = '',
  controlsVisible = false,
  nextHtml = undefined,
  prevHtml = undefined,
  nextClass = '',
  prevClass = '',
  onSlideChange = noop
}) => {
  const [activeIndex, setActiveIndex] = useState(startIndex)
  const [offsetState, setOffsetState] = useState(0)
  const [removeTransition, setRemoveTransition] = useState(true)
  const [inTransitionState, setInTransitionState] = useState(false)
  const numberOfSlides = Children.count(children)
  const slideRefs = useRef<RefObject<HTMLDivElement>[]>([])
  const intervalId = useRef<ReturnType<typeof setTimeout>>()
  const movingDirection = useRef('')
  const mouseOver = useRef(false)

  const [ mousedown, setMouseDown ] = useState(false)
  const [ dragOffset, setDragOffset ] = useState({ x: 0, y: 0 })
  const [ dragStart, setDragStart ] = useState({ x: 0, y: 0 })

  const updateIndex = (newIndex: number) => {
    if (newIndex < 0) {
      if (loop && !endLess) {
        newIndex = numberOfSlides - 1
      } else if (endLess) {
        newIndex = numberOfSlides - 1
      } else {
        newIndex = 0
      }
    } else if (newIndex >= numberOfSlides) {
      if (loop && !endLess) {
        newIndex = 0
      } else if (!loop) {
        newIndex = numberOfSlides - 1
      }
    }
    setActiveIndex(newIndex)
  }

  const moveSlidesForward = (activeIndex: number) => {
    if (loop && !endLess) {
      if (isLastSlide(activeIndex - 1)) {
        setOffsetState(0)
      } else {
        let offset = offsetState + getSlideWidth(activeIndex - 1)
        offset += doubleGap ? doubleGap * 2 : gap

        setOffsetState(offset)
      }
    } else {
      if (endLess) {
        if (getLastSlideIndex() + 1 >= activeIndex) {
          let offset = offsetState + getSlideWidth(activeIndex)
          offset += doubleGap ? doubleGap * 2 : gap

          setOffsetState(offset)
        }
      } else {
        if (!isLastSlide(activeIndex - 1)) {
          let offset = offsetState + getSlideWidth(activeIndex - 1)
          offset += doubleGap ? doubleGap * 2 : gap

          setOffsetState(offset)
        }
      }
    }
  }

  const moveSlidesBack = (activeIndex: number) => {
    if (loop && !endLess) {
      if (isFirstSlide(activeIndex + 1)) {
        setOffsetState(getSlidesTotalWidth())
      } else {
        let offset = offsetState - getSlideWidth(activeIndex)
        offset -= doubleGap ? doubleGap * 2 : gap

        setOffsetState(offset)
      }
    } else {
      if (endLess) {
        let offset = offsetState - getSlideWidth(activeIndex + 1)
        offset -= doubleGap ? doubleGap * 2 : gap

        setOffsetState(offset)
      } else {
        if (!isFirstSlide(activeIndex + 1)) {
          let offset = offsetState - getSlideWidth(activeIndex)
          offset -= doubleGap ? doubleGap * 2 : gap

          setOffsetState(offset)
        }
      }
    }
  }

  const getSlidesTotalWidth = () => {
    let totalWidth = 0

    for (let i = 0; i < numberOfSlides - 1; i++) {
      const gapFinal = doubleGap ? doubleGap * 2 : gap
      totalWidth += getSlideWidth(i) + gapFinal
    }

    return totalWidth
  }

  const setInitialOffset = () => {
    if (endLess) {
      setOffsetState(getSlideWidthWithGap(activeIndex))
    }
  }

  const calculateOffset = () => {
    let totalOffset = 0

    if (endLess) {
      totalOffset = getSlideWidthWithGap(activeIndex)
    }

    for (let i = 0; i < activeIndex; i++) {
      const gapFinal = doubleGap ? doubleGap * 2 : gap
      totalOffset += getSlideWidth(i) + gapFinal
    }

    setOffsetState(totalOffset)
  }

  const goNext = () => {
    if (inTransitionState) return

    movingDirection.current = 'forward'
    setInTransitionState(true)
    updateIndex(activeIndex + 1)
    moveSlidesForward(activeIndex + 1)
  }

  const goPrev = () => {
    if (inTransitionState) return

    movingDirection.current = 'backward'
    setInTransitionState(true)
    updateIndex(activeIndex - 1)
    moveSlidesBack(activeIndex - 1)
  }

  const calculatePosition = () => {
    return `translateX(-${offsetState}px)`
  }

  const createSlideRefs = () => {
    if (endLess) {
      let index = 0

      Children.forEach(children, () => {
        slideRefs.current[index] = slideRefs.current[index] || createRef<HTMLDivElement>()
        index++
      })

      Children.forEach(children, () => {
        slideRefs.current[index] = slideRefs.current[index] || createRef<HTMLDivElement>()
        index++
      })
    } else {
      Children.forEach(children, (_, i) => {
        slideRefs.current[i] = slideRefs.current[i] || createRef<HTMLDivElement>()
      })
    }
  }

  const createSlides = () => {
    if (endLess) {
      let index = 0
      const arrayOfChildren = Children.toArray(children)

      const slides = Children.map(arrayOfChildren, (child) => {
        if (isValidElement(child)) {
          const clone = cloneElement(child, {
            width: width,
            gap: isLastSlide(index) ? 0 : gap,
            doubleGap: doubleGap,
            key: index,
            ref: slideRefs.current[index],
            isActive: index === activeIndex
          })
          index++
          return clone
        }
      })

      const slidesCopy = Children.map(arrayOfChildren, (child, i) => {
        if (i === arrayOfChildren.length - 1) return

        if (isValidElement(child)) {
          const clone = cloneElement(child, {
            width: width,
            gap: isLastSlide(index) ? 0 : gap,
            doubleGap: doubleGap,
            key: index,
            ref: slideRefs.current[index],
            isActive: index === activeIndex
          })
          index++
          return clone
        }
      })

      const concatSlides = slides?.concat(slidesCopy)

      if (isValidElement(arrayOfChildren[arrayOfChildren.length - 1])) {
        //@ts-ignore
        const clone = cloneElement(arrayOfChildren[arrayOfChildren.length - 1], {
          width: width,
          gap: isLastSlide(index) ? 0 : gap,
          doubleGap: doubleGap,
          key: index,
          ref: slideRefs.current[index],
          isActive: index === activeIndex
        })

        concatSlides.unshift(clone)
      }

      return concatSlides

    } else {
      return Children.map(children, (child, i) => {
        if (isValidElement(child)) {
          return cloneElement(child, {
            width: width,
            gap: isLastSlide(i) ? 0 : gap,
            doubleGap: doubleGap,
            ref: slideRefs.current[i],
            isActive: i === activeIndex
          })
        }
      })
    }
  }

  const setupLoop = () => {
    intervalId.current = setInterval(() => {
      if (mouseOver.current) return
      document.getElementById('next')?.click()
    }, animationSpeed)
  }

  const removeLoop = () => {
    if (intervalId.current) {
      clearInterval(intervalId.current)
    }
  }

  const isLastSlide = (i: number) => {
    return numberOfSlides === i
  }

  const getLastSlideIndex = () => {
    return numberOfSlides - 1
  }

  const isFirstSlide = (i: number) => {
    return i === 0
  }

  const getSlideWidthWithGap = (i: number) => {
    const gapFinal = doubleGap ? doubleGap * 2 : gap
    return getSlideWidth(i) + gapFinal
  }

  const getSlideWidth = (i: number) => {
    if (!slideRefs.current[i]) return 0
    return slideRefs.current[i].current?.offsetWidth || 0
  }

  const handleMouseup = () => {
    setMouseDown(false)
    setDragOffset({
      x: 0,
      y: 0
    })
  }

  const handleMousedown = (e: React.MouseEvent) => {
    e.preventDefault()

    setDragStart({
      x: e.clientX,
      y: e.clientY
    })

    setMouseDown(true)
  }

  const handleTouchdown = (e: React.TouchEvent) => {
    setDragStart({
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    })

    setMouseDown(true)
  }

  const handleMousemove = (e: React.MouseEvent) => {
    if (!mousedown) {
      return
    }

    const eventPosX = e.clientX
    const eventPosY = e.clientY

    const deltaX = (dragStart.x - eventPosX)
    const deltaY = (dragStart.y - eventPosY)

    setDragOffset({ x: deltaX, y: deltaY })

    if (deltaX === 0) return

    const slope = Math.round(Math.abs(deltaY / deltaX + 0.1) * 10) / 10

    if (slope > 0.5) return

    if (dragOffset.x > minSwipeDistance) {
      handleMouseup()
      goNext()
    } else if (dragOffset.x < -minSwipeDistance) {
      handleMouseup()
      goPrev()
    }
  }

  const handleTouchmove = (e: React.TouchEvent) => {
    if (!mousedown) {
      return
    }

    const eventPosX = e.touches[0].clientX
    const eventPosY = e.touches[0].clientY

    const deltaX = (dragStart.x - eventPosX)
    const deltaY = (dragStart.y - eventPosY)

    setDragOffset({ x: deltaX, y: deltaY })

    if (deltaX === 0) return

    const slope = Math.round(Math.abs(deltaY / deltaX + 0.1) * 10) / 10

    if (slope > 0.5) return

    if (dragOffset.x > minSwipeDistance) {
      handleMouseup()
      goNext()
    } else if (dragOffset.x < -minSwipeDistance) {
      handleMouseup()
      goPrev()
    }
  }

  const handleTransitionEnd = () => {
    if (movingDirection.current === 'forward') {
      if (getLastSlideIndex() + 1 == activeIndex) {
        setRemoveTransition(true)
        updateIndex(0)
        setOffsetState(getSlideWidthWithGap(activeIndex))

        onSlideChange(0)

        setTimeout(() => {
          setRemoveTransition(false)
          setInTransitionState(false)
        }, 100)
      } else {
        setInTransitionState(false)

        onSlideChange(activeIndex)
      }
    } else if (movingDirection.current === 'backward') {
      if (isLastSlide(activeIndex + 1)) {
        setRemoveTransition(true)
        updateIndex(numberOfSlides - 1)
        setOffsetState(getSlidesTotalWidth() + getSlideWidthWithGap(activeIndex + 1))

        onSlideChange(numberOfSlides - 1)

        setTimeout(() => {
          setRemoveTransition(false)
          setInTransitionState(false)
        }, 100)
      } else {
        setInTransitionState(false)

        onSlideChange(activeIndex)
      }
    }
  }

  useEffect(() => {
    calculateOffset()
  }, [width])

  useEffect(() => {
    if (autoplay) {
      setupLoop()

      return () => {
        removeLoop()
      }
    }
  }, [activeIndex, autoplay])

  useEffect(() => {
    setInitialOffset()

    setTimeout(() => {
      setRemoveTransition(false)
    }, 100)
  }, [])

  createSlideRefs()

  return (
    <div
      className={styles.simple_carousel_wrapper}
      onMouseUp={() => {
        handleMouseup()
      }}
      onMouseOver={() => { mouseOver.current = true }}
      onMouseLeave={() => { mouseOver.current = false }}
    >
      <div
        className={`${styles.simple_carousel} ${containerClass ? containerClass : ''}` }
        style={{
          overflow: overflow ? overflow : undefined
        }}
      >
        <div
          className={`${styles.inner} ${!removeTransition ? styles.transition : ''}`}
          style={{
            transform: calculatePosition(),
            justifyContent: justifyBetween ? 'space-between' : undefined
          }}
          onTransitionEnd={() => {
            handleTransitionEnd()
          }}
          onMouseDown={(e) => {
            if (!disableControls) {
              handleMousedown(e)
            }
          }}
          onTouchStart={(e) => {
            if (!disableControls) {
              handleTouchdown(e)
            }
          }}
          onMouseMove={(e) => {
            if (!disableControls) {
              handleMousemove(e)
            }
          }}
          onTouchMove={(e) => {
            if (!disableControls) {
              handleTouchmove(e)
            }
          }}
        >
          {createSlides()}
        </div>
      </div>
      {controlsVisible &&
      <div className={styles.simple_carousel_controls}>
        <div
          className={prevClass}
          onClick={goPrev}
          onMouseOver={() => { mouseOver.current = true }}
          onMouseLeave={() => { mouseOver.current = false }}
        >
          {prevHtml}
        </div>
        <div
          className={nextClass}
          id="next"
          onClick={goNext}
          onMouseOver={() => { mouseOver.current = true }}
          onMouseLeave={() => { mouseOver.current = false }}
        >
          {nextHtml}
        </div>
      </div>}
      {bullets && <SimpleCarouselBullets total={numberOfSlides} current={activeIndex} bulletsClass={bulletsClass} />}
    </div>
  )
}

interface ItemProps {
  width?: string,
  children?: ReactElement,
  gap?: number,
  doubleGap?: number,
  isActive?: boolean,
  className?: string
}

export const SimpleCarouselItem = forwardRef<HTMLDivElement, ItemProps>(({ children, width, gap = 0, doubleGap = 0, isActive, className = '' }, ref) => {
  const style = {
    width: width,
    marginLeft: '',
    marginRight: ''
  }

  if (doubleGap) {
    style.marginLeft = `${doubleGap}px`
    style.marginRight = `${doubleGap}px`
  } else {
    style.marginRight = `${gap}px`
  }

  return (
    <div
      className={`${styles.simple_carousel_item} ${className} ${isActive ? 'simple-carousel-active' : ''}`}
      style={style}
      ref={ref}
    >
      {children}
    </div>
  )
})

SimpleCarouselItem.displayName = 'SimpleCarouselItem'

interface PropsBullets {
  total: number,
  current: number,
  bulletsClass?: string
}

export const SimpleCarouselBullets: React.FC<PropsBullets> = ({ total, current, bulletsClass = '' }) => {
  const classes = cn({
    [bulletsClass]: bulletsClass ? true : false,
    [styles.simple_carousel_bullets]: true
  })

  const bulletClasses = (n: number) => {
    if (n === current) return styles.simple_carousel_bullets_item_current
    if (n - 1 === current) return styles.simple_carousel_bullets_item_after_current
    if (n + 1 === current) return styles.simple_carousel_bullets_item_before_current
  }

  return (
    <div className={classes}>
      {
        // @ts-ignore
        [...Array(total).keys()].map((n) => {
          return <span key={n} className={`${styles.simple_carousel_bullets_item} ${bulletClasses(n)}`}></span>
        })
      }
    </div>
  )
}
